import { useCallback, useState } from "react";
import Draggable from 'react-draggable';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

import {
    ISchedule,
    ModalType,
    defaultSchedule,
} from "Pages/Setup/Scheduler/Scheduler";
import CronSelector from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/CronSelector";
import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import { convert } from "crontzconvert";

interface IProps {
    activeModal: ModalType;
    getSchedules: () => void;
    integrations: IIntegration[];
    onSetActiveModal: (modal: ModalType) => void;
    schedules: ISchedule[];
}

export const validNameRegex = /^[0-9a-zA-Z-_ ]{3,64}$/;

export default function AddSchedule({
    activeModal,
    getSchedules,
    integrations,
    onSetActiveModal,
    schedules,
}: IProps) {
    /** Handles name input validity causing valid/invalid styles to be applied  */
    const [nameValid, setNameValid] = useState<boolean | undefined>();

    /** The data in the add schedule modal that will be sent to back end */
    const [schedule, setSchedule] = useState<ISchedule>(defaultSchedule);
    /** Used to control how many integration inputs there are, and adding/removing them */
    const [integrationInputs, setIntegrationInputs] = useState<number>(
        schedule.integrations.length || 1
    );
    const [isRateInvalid, setIsRateInvalid] = useState(false);

    const { post } = useFetch();
    const { newAlert } = useAlertContext();

    const checkIfNameValid = useCallback(
        (name: string) => {
            // Name must contain at least three characters, and may only consists of
            // digits, letters, hyphens, underscores, spaces or periods. AWS does not allow
            // spaces, commas, etc. in names, but we allow spaces and replace them with underscores
            // on the front end.
            if (name.length < 3) {
                return;
            }

            const nameInUse = schedules.some(
                (schedule) => schedule.name.trim() === name.trim()
            );
            if (nameInUse) {
                setNameValid(false);
                return;
            }

            const valid = validNameRegex.test(name);

            setNameValid(valid);
        },
        [schedules]
    );

    const onSetSchedule = useCallback((schedule: ISchedule) => {
        setSchedule(schedule);
    }, []);

    const onSetIntegrationInputs = useCallback((inputs: number) => {
        setIntegrationInputs(inputs);
    }, []);

    const closeModal = () => {
        onSetActiveModal("");
        setIntegrationInputs(1);
        setSchedule(defaultSchedule);
        setNameValid(undefined);
        setIsRateInvalid(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (schedule.trigger.startsWith("rate")) {
            addRateSchedule();
        } else {
            addCronSchedule();
        }
    };

    const addRateSchedule = async () => {
        // AWS does not allow spaces. The regex matches one or more consecutive spaces.
        const nameWithUnderscores = schedule.name.trim().replace(/\s+/g, "_");

        const body = {
            ...schedule,
            name: nameWithUnderscores,
        };
        const response = await post("/api/schedule", body);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch (e) {
            console.log(e);
            data = response;
        }
        console.log("Add schedule response: ", data);

        if (response.ok) {
            newAlert({
                message: "Schedule added successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Unable to add schedule",
                color: "danger",
            });
        }
        closeModal();
    };

    const onSetIsRateInvalid = (isInvalid: boolean) => {
        setIsRateInvalid(isInvalid);
    };

    const addCronSchedule = async () => {
        // AWS does not allow spaces. The regex matches one or more consecutive spaces.
        const nameWithUnderscores = schedule.name.trim().replace(/\s+/g, "_");
        const triggerInMountainTimezone = convertCronUserTzToMountain(
            schedule.trigger
        );

        const body = {
            ...schedule,
            trigger: triggerInMountainTimezone,
            name: nameWithUnderscores,
        };
        const response = await post("/api/schedule", body);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch (e) {
            console.log(e);
            data = response;
        }
        console.log("Add schedule response: ", data);

        if (response.ok) {
            newAlert({
                message: "Schedule added successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Unable to add schedule",
                color: "danger",
            });
        }
        closeModal();
    };

    const cronSelectorProps = {
        checkIfNameValid,
        integrations,
        nameValid,
        onSetSchedule,
        parent: "add",
        schedule,
        integrationInputs,
        onSetIntegrationInputs,
        isRateInvalid,
        onSetIsRateInvalid,
    };

    /** Check if any selected integration no longer exists in the codebase */
    const hasStaleIntegrations = schedule.integrations.some((integration) => {
        const process = integration.split("/")[0];
        return process
            && process !== "Select Integration"
            && !integrations.some((int) => int.process === process);
    });

    /** Disables the submit button if schedule info has not been specified */
    const isDisabled =
        !nameValid ||
        schedule.name.length < 3 ||
        schedule.integrations.some((i) => i === "Select Integration") ||
        hasStaleIntegrations ||
        schedule.trigger === "" ||
        schedule.trigger.startsWith("*");

        
    return (
        <Draggable handle=".modal-header">
            <Modal isOpen={activeModal === "add"} toggle={closeModal}>
                <ModalHeader toggle={closeModal}>Add Schedule</ModalHeader>
                <ModalBody>
                    <CronSelector {...cronSelectorProps} />
                </ModalBody>
                <ModalFooter>
                    {hasStaleIntegrations && (
                        <div className="text-danger mb-2 w-100">
                            One or more selected integrations no longer exist. Please remove or replace them.
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isDisabled}
                    >
                        Submit
                    </Button>
                </ModalFooter>
            </Modal>
        </Draggable>
    );
}

export function convertCronUserTzToMountain(cron: string) {
    const mountainTimezone = "America/Denver";
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let cronInUserTz = convert(cron, userTimezone, mountainTimezone);
    // remove leading seconds added by crontzconvert and not used in react-js-cron or our back end
    cronInUserTz = cronInUserTz.split(" ").slice(1).join(" ");

    // The time zone converter library put days in a 0-6 system that does not work with AWS
    // 1-7 or SUN-SAT... so we need to convert it back
    const cronParts = cronInUserTz.split(" ");
    const days = cronParts[4];
    const newDays = days.replaceAll(/[0-6]/g, (day) => {
        const daysArray = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
        return daysArray[parseInt(day)];
    });
    cronParts[4] = newDays;
    cronInUserTz = cronParts.join(" ");

    return cronInUserTz;
}
